import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { toast } from "sonner";
import { AuthLayout } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { Input, StackField } from "@/components/ui/form";
import { Spinner } from "@/components/ui/panel";
import { client, errorMessage, unwrap } from "@/lib/api";
import { useMe, useRefreshMe } from "@/lib/session";

/** After 2FA, someone who signed in with a temporary password chooses their own. */
export function ChangePasswordPage() {
  const me = useMe();
  const refreshMe = useRefreshMe();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (me.isPending) return <Spinner />;
  if (!me.data) return <Navigate to="/login" replace />;
  if (!me.data.user.twoFactorEnabled) return <Navigate to="/setup-2fa" replace />;
  if (!me.data.user.mustChangePassword) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next.length < 12) return toast.error("新しいパスワードは12文字以上にしてください");
    if (next !== confirm) return toast.error("確認用のパスワードが一致しません");
    setBusy(true);
    try {
      await unwrap(client.me.password.$post({ json: { currentPassword: current, newPassword: next } }));
      toast.success("パスワードを変更しました");
      await refreshMe();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="パスワードの変更" lead="仮のパスワードでログインしました。ご自分だけが知っているパスワードに変更してください。">
      <form onSubmit={submit} className="space-y-4">
        <StackField label="仮のパスワード" required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus />}</StackField>
        <StackField label="新しいパスワード" required hint="12文字以上">{(id) => <Input id={id} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}</StackField>
        <StackField label="新しいパスワード（確認）" required>{(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}</StackField>
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!current || !next || !confirm}>変更してはじめる</Button>
      </form>
    </AuthLayout>
  );
}
